import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OSM from 'ol/source/OSM';
import { Draw } from 'ol/interaction';
import { createBox } from 'ol/interaction/Draw';
import { fromLonLat, toLonLat } from 'ol/proj';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { useEffect, useRef } from 'react';

type TerritoryPreviewMapProps = {
  initialCenter: [number, number];
  initialZoom: number;
  bbox: [number, number, number, number] | null;
  drawMode: boolean;
  onViewChange: (center: [number, number], zoom: number) => void;
  onBoxDrawn: (bbox: [number, number, number, number]) => void;
  flyTo?: { center: [number, number]; zoom: number; token: number } | null;
  className?: string;
};

const bboxStyle = new Style({
  fill: new Fill({ color: 'rgba(37, 99, 235, 0.15)' }),
  stroke: new Stroke({ color: '#2563eb', width: 2 }),
});

export function TerritoryPreviewMap({
  initialCenter,
  initialZoom,
  bbox,
  drawMode,
  onViewChange,
  onBoxDrawn,
  flyTo,
  className = '',
}: TerritoryPreviewMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  const bboxSourceRef = useRef<VectorSource | null>(null);
  const drawRef = useRef<Draw | null>(null);
  const onViewChangeRef = useRef(onViewChange);
  const onBoxDrawnRef = useRef(onBoxDrawn);
  onViewChangeRef.current = onViewChange;
  onBoxDrawnRef.current = onBoxDrawn;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const bboxSource = new VectorSource();
    bboxSourceRef.current = bboxSource;
    const map = new Map({
      target: containerRef.current,
      view: new View({ center: fromLonLat(initialCenter), zoom: initialZoom, minZoom: 2, maxZoom: 19 }),
      layers: [new TileLayer({ source: new OSM() }), new VectorLayer({ source: bboxSource, style: bboxStyle })],
      controls: [],
    });
    mapRef.current = map;
    map.on('moveend', () => {
      const view = map.getView();
      const center = view.getCenter();
      if (!center) return;
      const [lon, lat] = toLonLat(center);
      onViewChangeRef.current([lon, lat], view.getZoom() ?? initialZoom);
    });
    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const source = bboxSourceRef.current;
    if (!source) return;
    source.clear();
    if (!bbox) return;
    const [minLon, minLat, maxLon, maxLat] = bbox;
    const ring = [
      fromLonLat([minLon, minLat]),
      fromLonLat([maxLon, minLat]),
      fromLonLat([maxLon, maxLat]),
      fromLonLat([minLon, maxLat]),
      fromLonLat([minLon, minLat]),
    ];
    source.addFeature(new Feature({ geometry: new Polygon([ring]) }));
  }, [bbox]);

  useEffect(() => {
    const map = mapRef.current;
    const source = bboxSourceRef.current;
    if (!map || !source) return;
    if (!drawMode) {
      if (drawRef.current) {
        map.removeInteraction(drawRef.current);
        drawRef.current = null;
      }
      return;
    }
    const draw = new Draw({ source, type: 'Circle', geometryFunction: createBox() });
    draw.on('drawend', (evt) => {
      const geometry = evt.feature.getGeometry();
      const extent = geometry?.getExtent();
      if (extent) {
        const [minLon, minLat] = toLonLat([extent[0], extent[1]]);
        const [maxLon, maxLat] = toLonLat([extent[2], extent[3]]);
        onBoxDrawnRef.current([minLon, minLat, maxLon, maxLat]);
      }
      source.clear();
    });
    map.addInteraction(draw);
    drawRef.current = draw;
    return () => {
      map.removeInteraction(draw);
      drawRef.current = null;
    };
  }, [drawMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !flyTo) return;
    map.getView().animate({ center: fromLonLat(flyTo.center), zoom: flyTo.zoom, duration: 400 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyTo?.token]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const handle = () => map.updateSize();
    const timeout = setTimeout(handle, 50);
    window.addEventListener('resize', handle);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', handle);
    };
  });

  return <div ref={containerRef} className={`${className} ${drawMode ? 'cursor-crosshair' : ''}`} />;
}
